import { FileText } from "lucide-react";
import type { CleTableColumn } from "@cle/components/CleTable";
import type { SelectableEvidenceRow } from "@cle/models/selectableEvidenceRow";
import type { SelectableReportStepKey } from "@cle/views/indberetninger/components/RoundedReportOverview";

const MISSING_VALUE = "-";

const truncate = (value: string, maxLength = 20): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}...`;
};

const SHOW_FILE_NAME_COLUMN: Record<SelectableReportStepKey, boolean> = {
  courseCertificates: true,
  authorship: false,
  teaching: true,
  reductions: true,
  legalAid: true,
  dispensations: true,
};

const getDetailColumnValue = (
  row: SelectableEvidenceRow,
  columnId: string,
): string => {
  return (
    row.detailColumns.find((column) => column.id === columnId)?.value ??
    MISSING_VALUE
  );
};

export const getReportSubmissionTableColumns = (
  stepKey: SelectableReportStepKey,
  documents: SelectableEvidenceRow[],
): CleTableColumn<SelectableEvidenceRow>[] => {
  const showFileNameColumn = SHOW_FILE_NAME_COLUMN[stepKey];
  const detailColumnDefinitions = documents[0]?.detailColumns ?? [];
  const hasDetailColumns = detailColumnDefinitions.length > 0;

  const columns: CleTableColumn<SelectableEvidenceRow>[] = [];

  if (showFileNameColumn) {
    columns.push({
      id: "fileName",
      header: "Filnavn",
      title: (row) => row.fileName,
      cell: (row) => (
        <span className="d-inline-flex align-items-center gap-2">
          <FileText size={15} aria-hidden strokeWidth={1.75} />
          {hasDetailColumns ? truncate(row.fileName) : row.fileName}
        </span>
      ),
    });
  }

  detailColumnDefinitions.forEach((detailColumn) => {
    columns.push({
      id: detailColumn.id,
      header: detailColumn.label,
      title: (row) => getDetailColumnValue(row, detailColumn.id),
      cell: (row) => {
        const value = getDetailColumnValue(row, detailColumn.id);

        if (
          detailColumn.id === "courseTitle" ||
          detailColumn.id === "bookTitle"
        ) {
          return value === MISSING_VALUE ? MISSING_VALUE : truncate(value, 20);
        }

        return value;
      },
    });
  });

  columns.push({
    id: "lessons",
    header: "Lektioner",
    className: "text-end fw-bold",
    cell: (row) => row.lessons,
  });

  return columns;
};
